import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination, TextField, MenuItem,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";
import { hasPayloadFieldsManual } from "../../utils/payloadSchemaManual";

const ORIGIN_STYLE = {
    custom: { bgcolor: "#f0f9ff", color: "#0369a1" },
    system: { bgcolor: "#FFF8E1", color: "#b45309" },
    external: { bgcolor: "#fdf4ff", color: "#7e22ce" },
};

const TransactionTypesTab = ({
    transactionTypesApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [originFilter, setOriginFilter] = useState("all"); // all|custom|system|external
    const [sortBy, setSortBy] = useState("name_asc");

    const sourceData = Array.isArray(transactionTypesApi.data) ? transactionTypesApi.data : [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = sourceData.filter((et) => {
            if (activeFilter === "active" && !et.active) return false;
            if (activeFilter === "inactive" && et.active) return false;
            if (originFilter !== "all" && String(et.origin) !== originFilter) return false;
            if (!q) return true;
            return String(et.name || "").toLowerCase().includes(q)
                || String(et.key || "").toLowerCase().includes(q)
                || String(et.origin || "").toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "created_desc") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            if (sortBy === "updated_desc") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            return String(a.name || "").localeCompare(String(b.name || ""), "fr");
        });
        return rows;
    }, [sourceData, search, activeFilter, originFilter, sortBy]);

    if (transactionTypesApi.error) return <ErrorState error={transactionTypesApi.error} onRetry={() => transactionTypesApi.refetch()} />;
    if (transactionTypesApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (sourceData.length === 0) {
        return <EmptyState message="Aucun type de transaction configuré" color={SECTION_COLORS.transactionTypes.main} />;
    }

    const pageRaw = pages[4] || 0;
    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const page = Math.min(pageRaw, maxPage);
    const paginated = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher un type (name, key)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "created_desc", label: "Création (récent)" },
                    { value: "updated_desc", label: "Mise à jour (récent)" },
                ]}
            >
                <Box sx={{ minWidth: 180 }}>
                    <Typography fontSize={11} color="text.secondary" mb={0.25}>Origine</Typography>
                    <TextField
                        select
                        size="small"
                        value={originFilter}
                        onChange={(e) => setOriginFilter(e.target.value)}
                        sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                        <MenuItem value="all">Toutes</MenuItem>
                        <MenuItem value="custom">custom</MenuItem>
                        <MenuItem value="external">external</MenuItem>
                        <MenuItem value="system">system</MenuItem>
                    </TextField>
                </Box>
            </ListToolbar>
            <Grid container spacing={2}>
                {paginated.map((et) => {
                    const originStyle = ORIGIN_STYLE[et.origin] || { bgcolor: "#f5f5f5", color: "#555" };
                    const hasSchema = hasPayloadFieldsManual(et);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={et.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.transactionTypes.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.transactionTypes.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.transactionTypes.main}30`,
                                },
                            }}>
                                {/* Origin badge */}
                                <Tooltip title={`Origine : ${et.origin}`} placement="left">
                                    <Chip label={et.origin} size="small"
                                        sx={{
                                            position: "absolute", top: 10, right: 10,
                                            fontWeight: 700, fontSize: 10, ...originStyle,
                                        }} />
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={9}>
                                    <EventNoteIcon sx={{ color: SECTION_COLORS.transactionTypes.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {et.name}
                                    </Typography>
                                </Box>

                                {/* Key + brand */}
                                <Chip label={et.key} size="small"
                                    sx={{
                                        mb: 1, fontSize: 10, fontWeight: 600,
                                        bgcolor: SECTION_COLORS.transactionTypes.light,
                                        color: SECTION_COLORS.transactionTypes.main, maxWidth: "100%"
                                    }} />

                                {et.description && (
                                    <Typography fontSize={11} color="text.secondary" mb={1.5} noWrap sx={{ fontStyle: "italic" }}>
                                        {et.description}
                                    </Typography>
                                )}

                                {/* Meta */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {et.brand && (
                                        <Chip label={et.brand} size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                    )}
                                    {hasSchema && (
                                        <Chip label="Schéma" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                                    )}
                                </Box>

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={et.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(et)}
                                                sx={{
                                                    color: SECTION_COLORS.transactionTypes.main, bgcolor: `${SECTION_COLORS.transactionTypes.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.transactionTypes.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(et)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(et)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={filtered.length} page={page}
                    onPageChange={handlePageChange(4)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default TransactionTypesTab;
